/**
 * Example Datasets Registry
 *
 * Unified registry of example code templates organized by dataset key.
 * Each dataset contains usage types that can be displayed via TemplateCodeBlock.
 */

export interface UsageType {
  id: string;
  label: string;
  language: 'bash' | 'json' | 'typescript' | 'python' | 'powershell';
  code: string | ((params: { apiKey: string; baseUrl: string }) => string);
}

export interface ExampleDataset {
  title: string;
  /** Plain-text description; `backticks` render as inline code. */
  intro?: string;
  usageTypes: UsageType[];
}

export const EXAMPLE_DATASETS: Record<string, ExampleDataset> = {
  rest_examples: {
    title: 'Integration examples',
    intro:
      'Here are some example code snippets to help you get started with reading from and writing to your DeadDrop topic using the REST API. Consult the DeadDrop API documentation for more details on request methods, parameters and response formats.',
    usageTypes: [
      {
        id: 'curl',
        label: 'cURL',
        language: 'bash',
        code: ({ apiKey, baseUrl }) =>
          `# write to topic\n` +
          `curl -X POST -H "Content-Type: application/json" \\\n` +
          `  -d '{"payload": "Hello World!"}' \\\n` +
          `  "${baseUrl}/v1/messages?api_key=${apiKey}"\n` +
          `\n` +
          `# read from topic (returns default configured content type)\n` +
          `curl "${baseUrl}/v1/messages?api_key=${apiKey}"`,
      },
      {
        id: 'axios-write',
        label: 'JavaScript/TypeScript - Axios',
        language: 'typescript',
        code: ({ apiKey, baseUrl }) =>
          `import axios from 'axios';\n\n` +
          `const url = '${baseUrl}/v1/messages';\n` +
          `const headers = {\n` +
          `  'X-API-Key': '${apiKey}'\n` +
          `};\n\n` +
          `// read from topic (returns default configured content type)\n` +
          `const readResponse = await axios.get(url, { headers });\n\n` +
          `// write to topic\n` +
          `const writeResponse = await axios.post(url, { payload: 'Hello World!' }, { headers });`,
      },
      {
        id: 'fetch',
        label: 'JavaScript/TypeScript - Fetch',
        language: 'typescript',
        code: ({ apiKey, baseUrl }) =>
          `import axios from 'axios';\n\n` +
          `const url = '${baseUrl}/v1/messages';\n` +
          `const headers = {\n` +
          `  'X-API-Key': '${apiKey}'\n` +
          `};\n\n` +
          `// read from topic (returns default configured content type)\n` +
          `const readResponse = await fetch(url, { headers });\n\n` +
          `// write to topic\n` +
          `const writeResponse = await fetch(url, {\n` +
          `  headers,\n` +
          `  method: 'POST',\n` +
          `  body: JSON.stringify({ payload: 'Hello World!' })\n` +
          `});`,
      },
      {
        id: 'python-requests',
        label: 'Python Requests',
        language: 'python',
        code: ({ apiKey, baseUrl }) =>
          `import requests\n\n` +
          `url = '${baseUrl}/v1/messages';\n` +
          `headers = {\n` +
          `    X-API-Key: '${apiKey}'\n` +
          `},\n\n` +
          `# read from topic\n` +
          `read_response = requests.get(url, headers=headers)\n\n` +
          `# write to topic\n` +
          `write_response = requests.post(\n` +
          `    url,\n` +
          `    headers=headers,\n` +
          `    json={ 'payload': 'Hello World!' },\n` +
          `)`,
      },
      {
        id: 'powershell',
        label: 'PowerShell',
        language: 'powershell',
        code: ({ apiKey, baseUrl }) =>
          `$url = "${baseUrl}/v1/messages"\n` +
          `$headers = @{\n` +
          `    "X-API-Key" = "${apiKey}"\n` +
          `}\n\n` +
          `# read from topic\n` +
          `Invoke-RestMethod -Uri $url -Headers $headers\n\n` +
          `# write to topic\n` +
          `$body = @{\n` +
          `    payload = "Hello World!"\n` +
          `} | ConvertTo-Json\n\n` +
          `Invoke-RestMethod -Method Post -Uri $url -Headers $headers -Body $body`,
      },
    ],
  },

  mcp_examples: {
    title: 'Setup and configuration examples',
    usageTypes: [
      {
        id: 'generic',
        label: 'Generic MCP configuration',
        language: 'json',
        code: ({ apiKey, baseUrl }) =>
          JSON.stringify(
            {
              mcpServers: {
                deaddrop: {
                  type: 'http',
                  url: `${baseUrl}/mcp`,
                  headers: {
                    'X-API-Key': apiKey,
                  },
                },
              },
            },
            null,
            2
          ),
      },
      {
        id: 'claude-code-linux-mac',
        label: 'Claude Code (Linux/Mac)',
        language: 'bash',
        code: ({ apiKey, baseUrl }) =>
          `claude mcp add deaddrop ${baseUrl}/mcp \\\n` +
          `  --scope project \\\n` +
          `  --transport http \\\n` +
          `  --header "X-API-Key: ${apiKey}"`,
      },
      {
        id: 'claude-code-powershell',
        label: 'Claude Code (Windows/PowerShell)',
        language: 'powershell',
        code: ({ apiKey, baseUrl }) =>
          `claude mcp add deaddrop ${baseUrl}/mcp \`\n` +
          `  --scope project \`\n` +
          `  --transport http \`\n` +
          `  --header "X-API-Key: ${apiKey}"`,
      },
    ],
  },

  webhook_verification: {
    title: 'Webhook signature verification',
    intro:
      'Verify webhook signatures using HMAC-SHA256. Compare the `X-Webhook-Signature` header against the computed hash of the request body.',
    usageTypes: [
      {
        id: 'python',
        label: 'Python',
        language: 'python',
        code: `import hmac
import hashlib

def verify_webhook(request_body: bytes, signature: str, secret: str) -> bool:
    expected = hmac.new(
        secret.encode(),
        request_body,
        hashlib.sha256
    ).hexdigest()
    return hmac.compare_digest(signature, expected)`,
      },
      {
        id: 'typescript',
        label: 'TypeScript',
        language: 'typescript',
        code: `import crypto from 'crypto';

function verifyWebhook(
  requestBody: string,
  signature: string,
  secret: string
): boolean {
  const expected = crypto
    .createHmac('sha256', secret)
    .update(requestBody)
    .digest('hex');

  return crypto.timingSafeEqual(
    Buffer.from(signature),
    Buffer.from(expected)
  );
}`,
      },
    ],
  },
};
